import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUp } from "lucide-react";
import Isotipo from "./Isotipo";
import { CONTACT, UI } from "@/data/site";
import { useLang } from "@/context/LanguageContext";

const Footer = () => {
  const { t, lp } = useLang();
  const year = new Date().getFullYear();
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="relative bg-ink-3 border-t border-white/10 overflow-hidden" data-testid="site-footer">
      {/* top block */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Isotipo className="w-7 h-7" fill="#edaa51" />
            <span className="font-display text-xl font-black uppercase tracking-tight text-white">Grabarte</span>
          </div>
          <p className="mt-6 max-w-sm text-white/50 font-body leading-relaxed">
            {t({
              es: "Agencia de marketing, comunicación y eventos. Estudio de desarrollo audiovisual. Marcamos la diferencia desde Madrid.",
              en: "Marketing, communication & events agency. Audiovisual development studio. Making the difference from Madrid.",
            })}
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-5">{t({ es: "Explora", en: "Explore" })}</h4>
          <ul className="space-y-3 font-body">
            <li><Link className="text-white/70 hover:text-brand-green transition-colors" to={lp("/marketing-comunicacion-eventos")}>{t(UI.nav.marketing)}</Link></li>
            <li><Link className="text-white/70 hover:text-brand-gold transition-colors" to={lp("/produccion-audiovisual")}>{t(UI.nav.audiovisual)}</Link></li>
            <li><Link className="text-white/70 hover:text-white transition-colors" to={lp("/proyectos")}>{t(UI.nav.proyectos)}</Link></li>
            <li><Link className="text-white/70 hover:text-white transition-colors" to={lp("/actualidad")}>{t(UI.nav.actualidad)}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-display text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-5">{t(UI.nav.contacto)}</h4>
          <ul className="space-y-4 font-body">
            <li><a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors" data-testid="footer-phone"><Phone size={16} className="text-brand-gold" /> {CONTACT.phone}</a></li>
            <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors" data-testid="footer-email"><Mail size={16} className="text-brand-gold" /> {CONTACT.email}</a></li>
            <li className="flex items-start gap-3 text-white/80"><MapPin size={16} className="text-brand-gold mt-1 shrink-0" /> <span>{CONTACT.address}<br />{CONTACT.city}</span></li>
          </ul>
        </div>
      </div>

      {/* kinetic wordmark */}
      <div className="relative border-t border-white/10">
        <h2 className="font-display font-black uppercase text-white/[0.06] leading-none text-center select-none py-6" style={{ fontSize: "clamp(3.5rem, 17vw, 18rem)" }}>
          grabarte
        </h2>
        <button
          onClick={toTop}
          data-testid="back-to-top"
          aria-label="Back to top"
          className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-brand-gold text-ink flex items-center justify-center hover:scale-110 transition-transform"
        >
          <ArrowUp size={22} />
        </button>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-sm font-body">© {year} Grabarte. {t({ es: "Todos los derechos reservados.", en: "All rights reserved." })}</p>
          <div className="flex items-center gap-6 text-white/40 text-sm font-body">
            <a href="https://grabarte360.com/aviso-legal/" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors" data-testid="footer-legal">{t({ es: "Aviso legal", en: "Legal" })}</a>
            <a href="https://grabarte360.com/politica-de-privacidad/" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors" data-testid="footer-privacy">{t({ es: "Privacidad", en: "Privacy" })}</a>
            <a href="https://grabarte360.com/politica-de-cookies/" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors" data-testid="footer-cookies">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
